import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

// The site key is public by design — it's in every page load either way. Dev
// builds use Cloudflare's always-pass test key so the widget never blocks
// local work. The secret lives in SSM (/loonworks/turnstile-secret), read by
// the contact-form Lambda.
const TURNSTILE_SITE_KEY = import.meta.env.DEV
  ? '1x00000000000000000000AA'
  : '0x4AAAAAAEYgkzVbm1JIIgp9'

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  reset: (id: string) => void
  remove: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let loader: Promise<TurnstileApi> | null = null

function loadTurnstile(): Promise<TurnstileApi> {
  loader ??= new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile)
      return
    }
    const script = document.createElement('script')
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error('Turnstile loaded without initializing'))
    script.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(script)
  })
  return loader
}

export interface TurnstileHandle {
  // Tokens are single-use: any submit that reached the server consumed the
  // token, so a failed submit must reset the widget for a fresh one.
  reset: () => void
}

interface TurnstileProps {
  onToken: (token: string) => void
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  function Turnstile({ onToken }, ref) {
    const slot = useRef<HTMLDivElement>(null)
    const widget = useRef<string | null>(null)
    const latestOnToken = useRef(onToken)
    latestOnToken.current = onToken

    useImperativeHandle(ref, () => ({
      reset() {
        if (widget.current !== null) window.turnstile?.reset(widget.current)
      },
    }))

    useEffect(() => {
      let cancelled = false
      loadTurnstile()
        .then((turnstile) => {
          if (cancelled || !slot.current || widget.current !== null) return
          widget.current = turnstile.render(slot.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'light',
            // Invisible unless Cloudflare needs the visitor to do something:
            // the default appearance parks a green "Success!" badge above the
            // submit button, which reads as an already-sent form.
            appearance: 'interaction-only',
            callback: (token: string) => latestOnToken.current(token),
            'expired-callback': () => latestOnToken.current(''),
            'error-callback': () => latestOnToken.current(''),
          })
        })
        .catch(() => {
          // Script blocked or offline: the slot stays empty and the server
          // rejects the tokenless submit with its own friendly message.
        })
      return () => {
        cancelled = true
        if (widget.current !== null) {
          window.turnstile?.remove(widget.current)
          widget.current = null
        }
      }
    }, [])

    return <div ref={slot} />
  },
)
